import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { 
  X, 
  User, 
  Mail, 
  Calendar, 
  Settings, 
  Brain, 
  Save, 
  AlertCircle,
  Eye,
  EyeOff,
  Lock,
  Shield,
  Database,
  Cloud,
  Trash2,
  RefreshCw
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { authAPI } from '../../services/api'
import { ButtonSpinner } from '../../components/common/LoadingSpinner'
import CloudStorageSettings from '../cloud/CloudStorageSettings'
import { getCleanupService } from '../../services/googleDriveCleanup'
import googleDriveAPI from '../../services/googleDriveAPI'
import { useStatus } from '../../contexts/StatusContext'
import backupService from '../../services/backupService'
import toast from 'react-hot-toast'

/**
 * Settings Modal Component
 * 
 * Modal dialog that combines profile information and settings including:
 * - Account information display
 * - Change password functionality  
 * - AI configuration settings
 */
const SettingsModal = ({ isOpen, onClose }) => {
  const { user, changePassword } = useAuth()
  const { isLocalMode, loadLocalData } = useLocalMode()
  const { showLoading, showSuccess, showError, showWarning, showInfo, dismissStatus } = useStatus()
  const [activeTab, setActiveTab] = useState(isLocalMode ? 'backup' : 'profile')
  
  // AI Configuration state
  const [aiConfig, setAiConfig] = useState({
    aiUrl: '',
    aiToken: '',
    aiModel: ''
  })
  const [isLoadingAiConfig, setIsLoadingAiConfig] = useState(true)
  const [isSavingAiConfig, setIsSavingAiConfig] = useState(false)

  // Password change state
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  // Cleanup state
  const [isCleaningUp, setIsCleaningUp] = useState(false)
  const [cleanupStats, setCleanupStats] = useState(null)
  const [orphanedFilesStats, setOrphanedFilesStats] = useState(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [isLoadingOrphanedStats, setIsLoadingOrphanedStats] = useState(false)
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false)

  // Backup and restore state
  const [isCreatingBackup, setIsCreatingBackup] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const [restoreProgress, setRestoreProgress] = useState({ percent: 0, message: '' })
  const [selectedFile, setSelectedFile] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    reset
  } = useForm({
    defaultValues: {
      email: user?.email || '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  })

  const newPassword = watch('newPassword')

  // Load AI configuration when modal opens (only in cloud mode)
  useEffect(() => {
    if (isOpen) {
      if (!isLocalMode) {
        loadAIConfiguration()
      }
      setPasswordChangeSuccess(false)
    }
  }, [isOpen, isLocalMode])

  // Update active tab when local mode changes
  useEffect(() => {
    if (isLocalMode && activeTab !== 'backup') {
      setActiveTab('backup')
    } else if (!isLocalMode && activeTab === 'backup') {
      setActiveTab('profile')
    }
  }, [isLocalMode, activeTab])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        email: user?.email || '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setActiveTab(isLocalMode ? 'backup' : 'profile')
      setPasswordChangeSuccess(false)
    }
  }, [isOpen, reset, user, isLocalMode])

  const loadAIConfiguration = async () => {
    try {
      setIsLoadingAiConfig(true)
      const config = await authAPI.getAIConfiguration()
      setAiConfig({
        aiUrl: config.aiUrl || '',
        aiToken: config.aiToken || '',
        aiModel: config.aiModel || ''
      })
    } catch (error) {
      console.error('Failed to load AI configuration:', error)
      toast.error('Failed to load AI configuration')
    } finally {
      setIsLoadingAiConfig(false)
    }
  }

  const handleAiConfigChange = (field, value) => {
    setAiConfig(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const saveAIConfiguration = async () => {
    try {
      setIsSavingAiConfig(true)
      await authAPI.updateAIConfiguration(aiConfig)
      toast.success('AI configuration saved successfully!')
    } catch (error) {
      console.error('Failed to save AI configuration:', error)
      toast.error('Failed to save AI configuration')
    } finally {
      setIsSavingAiConfig(false)
    }
  }

  const onPasswordSubmit = async (data) => {
    try {
      setIsChangingPassword(true)
      clearErrors()

      const result = await changePassword({
        email: data.email,
        oldPassword: data.oldPassword,
        newPassword: data.newPassword
      })
      
      if (result.success) {
        setPasswordChangeSuccess(true)
        reset({
          email: user?.email || '',
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        toast.success('Password changed successfully!')
      } else {
        if (result.error.toLowerCase().includes('current') || result.error.toLowerCase().includes('old')) {
          setError('oldPassword', { message: result.error })
        } else if (result.error.toLowerCase().includes('new')) {
          setError('newPassword', { message: result.error })
        } else {
          setError('root', { message: result.error })
        }
      }
    } catch (error) {
      setError('root', { 
        message: 'An unexpected error occurred. Please try again.' 
      })
    } finally {
      setIsChangingPassword(false)
    }
  }

  /**
   * Load Google Drive cleanup statistics
   */
  const loadCleanupStats = async () => {
    if (isLocalMode) return
    
    try {
      setIsLoadingStats(true)
      const cleanupService = getCleanupService(googleDriveAPI)
      const stats = await cleanupService.getCleanupStats()
      setCleanupStats(stats)
    } catch (error) {
      console.error('❌ Failed to load cleanup stats:', error)
      setCleanupStats(null)
    } finally {
      setIsLoadingStats(false)
    }
  }

  /**
   * Load orphaned files statistics
   */
  const loadOrphanedFilesStats = async () => {
    if (isLocalMode) return
    
    try {
      setIsLoadingOrphanedStats(true)
      const cleanupService = getCleanupService(googleDriveAPI)
      const stats = await cleanupService.getOrphanedFilesStats()
      setOrphanedFilesStats(stats)
    } catch (error) {
      console.error('❌ Failed to load orphaned files stats:', error)
      setOrphanedFilesStats(null)
    } finally {
      setIsLoadingOrphanedStats(false)
    }
  }

  /**
   * Handle cleanup of corrupted Google Drive files
   */
  const handleCleanupFiles = async () => {
    if (isLocalMode) return
    
    try {
      setIsCleaningUp(true)
      console.log('🧹 SettingsModal: Starting Google Drive cleanup...')
      
      const cleaningId = showLoading('🧹 Scanning for corrupted files...')
      
      // Get cleanup service
      const cleanupService = getCleanupService(googleDriveAPI)
      
      // First, get stats
      const stats = await cleanupService.getCleanupStats()
      dismissStatus(cleaningId)
      
      if (stats.suspiciousFiles === 0) {
        showSuccess(`✅ No corrupted files found! Your Google Drive is clean. (${stats.totalFiles} total files)`)
        setCleanupStats(stats)
        return
      }
      
      showWarning(`Found ${stats.suspiciousFiles} suspicious files that may be causing backend errors.`)
      
      // Run cleanup
      const cleanupId = showLoading('🧹 Cleaning up corrupted files...')
      const result = await cleanupService.cleanupCorruptedFiles()
      dismissStatus(cleanupId)
      
      if (result.cancelled) {
        showInfo('Cleanup cancelled by user')
        return
      }
      
      if (result.cleaned > 0) {
        showSuccess(`✅ Cleanup complete! Moved ${result.cleaned} corrupted files to trash. Backend performance should be much better now!`)
        
        // Refresh stats after cleanup
        setTimeout(() => {
          loadCleanupStats()
        }, 1000)
      } else {
        showInfo('No files were cleaned up')
      }
      
    } catch (error) {
      console.error('❌ SettingsModal: Cleanup failed:', error)
      showError(`Cleanup failed: ${error.message}`)
    } finally {
      setIsCleaningUp(false)
    }
  }

  /**
   * Handle cleanup of duplicate/corrupted Google Drive folders
   */
  const handleCleanupFolders = async () => {
    if (isLocalMode) return
    
    try {
      setIsCleaningUp(true)
      console.log('🧹 SettingsModal: Starting Google Drive folder cleanup...')
      
      const cleaningId = showLoading('🧹 Scanning for duplicate folders...')
      
      // Get cleanup service
      const cleanupService = getCleanupService(googleDriveAPI)
      
      // Run folder cleanup
      const result = await cleanupService.cleanupDuplicateFolders()
      dismissStatus(cleaningId)
      
      if (result.success) {
        if (result.deletedCount > 0) {
          showSuccess(`✅ Folder cleanup complete! Deleted ${result.deletedCount} duplicate/corrupted folders. Your Google Drive structure has been reset.`)
          showInfo('🔄 Please refresh the page to let the system recreate the folder structure properly.')
        } else {
          showSuccess('✅ No duplicate folders found. Your Google Drive structure is clean.')
        }
      } else {
        showError(`Folder cleanup failed: ${result.message}`)
      }
      
    } catch (error) {
      console.error('❌ SettingsModal: Folder cleanup failed:', error)
      showError(`Folder cleanup failed: ${error.message}`)
    } finally {
      setIsCleaningUp(false)
    }
  }

  /**
   * Handle cleanup of orphaned images and attachments
   */
  const handleCleanupOrphanedFiles = async () => {
    if (isLocalMode) return
    
    try {
      setIsCleaningUp(true)
      console.log('🧹 SettingsModal: Starting orphaned files cleanup...')
      
      const cleaningId = showLoading('🧹 Scanning for orphaned images and attachments...')
      
      // Get cleanup service
      const cleanupService = getCleanupService(googleDriveAPI)
      
      // First, get stats
      const stats = await cleanupService.getOrphanedFilesStats()
      dismissStatus(cleaningId)
      
      if (stats.orphanedImages === 0 && stats.orphanedAttachments === 0 && stats.orphanedCacheEntries === 0) {
        showSuccess(`✅ No orphaned files found! All images and attachments are properly referenced.`)
        setOrphanedFilesStats(stats)
        return
      }
      
      showWarning(`Found ${stats.orphanedImages} orphaned images, ${stats.orphanedAttachments} orphaned attachments, and ${stats.orphanedCacheEntries} orphaned cache entries.`)
      
      // Run cleanup
      const cleanupId = showLoading('🧹 Cleaning up orphaned files...')
      const result = await cleanupService.cleanupOrphanedFiles()
      dismissStatus(cleanupId)
      
      if (result.cancelled) {
        showInfo('Orphaned files cleanup cancelled by user')
        return
      }
      
      if (result.cleanedImages > 0 || result.cleanedAttachments > 0 || result.cleanedCacheEntries > 0) {
        const totalCleaned = result.cleanedImages + result.cleanedAttachments + result.cleanedCacheEntries
        showSuccess(`✅ Orphaned files cleanup complete! Deleted ${result.cleanedImages} orphaned images, ${result.cleanedAttachments} orphaned attachments, and ${result.cleanedCacheEntries} orphaned cache entries. Total: ${totalCleaned} files cleaned up.`)
        
        // Refresh stats after cleanup
        setTimeout(() => {
          loadOrphanedFilesStats()
        }, 1000)
      } else {
        showInfo('No orphaned files were cleaned up')
      }
      
    } catch (error) {
      console.error('❌ SettingsModal: Orphaned files cleanup failed:', error)
      showError(`Orphaned files cleanup failed: ${error.message}`)
    } finally {
      setIsCleaningUp(false)
    }
  }

  /**
   * Handle emptying the soft-delete folder (recycle bin)
   */
  const handleEmptyBin = async () => {
    if (isLocalMode) return
    
    // Show warning and ask for confirmation
    showWarning('⚠️ This action will permanently delete ALL files from the recycle bin. This cannot be undone!')
    
    const confirmed = window.confirm(
      '🚨 WARNING: Empty Recycle Bin\n\n' +
      'This will PERMANENTLY DELETE all files from the soft-delete folder.\n\n' +
      '• This action CANNOT be undone\n' +
      '• All deleted notes will be lost forever\n' +
      '• Storage space will be freed up\n\n' +
      'Are you absolutely sure you want to continue?'
    )
    
    if (!confirmed) {
      showInfo('Empty bin operation cancelled')
      return
    }
    
    try {
      setIsCleaningUp(true)
      console.log('🗑️ SettingsModal: Starting soft-delete folder cleanup...')
      
      const cleaningId = showLoading('🗑️ Emptying recycle bin...')
      
      // Get cleanup service
      const cleanupService = getCleanupService(googleDriveAPI)
      
      // Run soft-delete cleanup
      const result = await cleanupService.cleanupSoftDeletedNotes()
      dismissStatus(cleaningId)
      
      if (result.success) {
        if (result.deletedCount > 0) {
          showSuccess(`✅ Recycle bin emptied! Permanently deleted ${result.deletedCount} files from soft-delete folder.`)
          showInfo('💾 Storage space has been freed up. This action cannot be undone.')
        } else {
          showSuccess('✅ Recycle bin is already empty. No files to delete.')
        }
        
        // Refresh stats after cleanup
        setTimeout(() => {
          loadCleanupStats()
        }, 1000)
      } else {
        showError(`Failed to empty recycle bin: ${result.message}`)
      }
      
    } catch (error) {
      console.error('❌ SettingsModal: Empty bin failed:', error)
      showError(`Failed to empty recycle bin: ${error.message}`)
    } finally {
      setIsCleaningUp(false)
    }
  }

  // Load cleanup stats when cloud tab is opened
  useEffect(() => {
    if (activeTab === 'cloud' && !isLocalMode && !cleanupStats && !isLoadingStats) {
      loadCleanupStats()
    }
  }, [activeTab, isLocalMode])

  // Load orphaned files stats when cloud tab is opened
  useEffect(() => {
    if (activeTab === 'cloud' && !isLocalMode && !orphanedFilesStats && !isLoadingOrphanedStats) {
      loadOrphanedFilesStats()
    }
  }, [activeTab, isLocalMode])

  /**
   * Handle backup creation
   */
  const handleCreateBackup = async () => {
    if (!isLocalMode) return
    
    try {
      setIsCreatingBackup(true)
      console.log('🔄 SettingsModal: Starting backup creation...')
      
      // Show user warning about sensitive content encryption
      showInfo('📋 The sensitive note content will remain encrypted and can only be decrypted with the original password used to encrypt each note.')
      
      const loadingId = showLoading('📦 Creating backup of your notes...')
      
      // Create backup
      const backupBlob = await backupService.createBackup()
      const filename = backupService.generateBackupFilename()
      
      // Download the backup
      backupService.downloadBlob(backupBlob, filename)
      
      dismissStatus(loadingId)
      showSuccess(`✅ Backup created successfully! Downloaded as "${filename}"`)
      
      console.log('✅ SettingsModal: Backup created and downloaded successfully')
      
    } catch (error) {
      console.error('❌ SettingsModal: Backup creation failed:', error)
      showError(`Backup failed: ${error.message}`)
    } finally {
      setIsCreatingBackup(false)
    }
  }

  /**
   * Handle file selection for restore
   */
  const handleFileSelection = (event) => {
    const file = event.target.files[0]
    if (file) {
      console.log('📁 SettingsModal: File selected for restore:', file.name)
      setSelectedFile(file)
    }
  }

  /**
   * Handle backup restore
   */
  const handleRestoreBackup = async () => {
    if (!isLocalMode || !selectedFile) return
    
    try {
      setIsRestoring(true)
      setRestoreProgress({ percent: 0, message: 'Starting restore...' })
      
      console.log('🔄 SettingsModal: Starting backup restore...')
      
      // Show initial progress
      setRestoreProgress({ percent: 5, message: 'Validating backup file...' })
      
      // Validate backup file
      await backupService.validateBackupFile(selectedFile)
      
      setRestoreProgress({ percent: 10, message: 'Cleaning up existing duplicates...' })
      
      // Clean up any existing duplicate folders first
      await backupService.cleanupLocalFolderDuplicates()
      
      setRestoreProgress({ percent: 15, message: 'Backup file validated successfully...' })
      
      // Restore backup with progress tracking
      const result = await backupService.restoreBackup(selectedFile, (percent, message) => {
        setRestoreProgress({ percent, message })
      })
      
      // Set final progress for refresh
      setRestoreProgress({ percent: 95, message: 'Refreshing notes list...' })
      
      // Refresh local data to show restored notes immediately
      console.log('🔄 SettingsModal: Refreshing local data to show restored notes...')
      await loadLocalData()
      
      // Show detailed results
      const { mergeResults } = result
      const totalAdded = mergeResults.notes.added + mergeResults.folders.added + mergeResults.attachments.added
      const totalSkipped = mergeResults.notes.skipped + mergeResults.folders.skipped + mergeResults.attachments.skipped
      
      setRestoreProgress({ percent: 100, message: 'Restore completed successfully!' })
      
      // Wait a moment to show completion
      await new Promise(resolve => setTimeout(resolve, 500))
      
      let successMessage = `✅ Restore completed! Added ${totalAdded} new items.`
      if (totalSkipped > 0) {
        successMessage += ` Skipped ${totalSkipped} existing items (no overwrites).`
      }
      
      // Show success notification
      showSuccess(successMessage)
      
      // Show detailed breakdown in console
      console.log('📊 SettingsModal: Restore results:', {
        notes: `${mergeResults.notes.added} added, ${mergeResults.notes.skipped} skipped`,
        folders: `${mergeResults.folders.added} added, ${mergeResults.folders.skipped} skipped`,
        attachments: `${mergeResults.attachments.added} added, ${mergeResults.attachments.skipped} skipped`
      })
      
      // Clear selected file
      setSelectedFile(null)
      const fileInput = document.querySelector('input[type="file"][accept=".zip"]')
      if (fileInput) fileInput.value = ''
      
      // Auto-close settings modal after successful restore
      console.log('✅ SettingsModal: Auto-closing settings modal after successful restore')
      setTimeout(() => {
        onClose()
      }, 1000) // Give user time to see the success message
      
    } catch (error) {
      console.error('❌ SettingsModal: Restore failed:', error)
      showError(`Restore failed: ${error.message}`)
      setRestoreProgress({ percent: 0, message: 'Restore failed' })
    } finally {
      setIsRestoring(false)
      // Clear progress after a delay if there was an error
      setTimeout(() => {
        setRestoreProgress({ percent: 0, message: '' })
      }, 2000)
    }
  }

  const tabs = isLocalMode 
    ? [{ id: 'backup', label: 'Backup and Restore', icon: Database }]
    : [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'security', label: 'Security', icon: Lock },
        { id: 'ai', label: 'AI Settings', icon: Brain },
        { id: 'cloud', label: 'Cloud Storage', icon: Cloud },
        { id: 'backup', label: 'Backup and Restore', icon: Database }
      ]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg sm:rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 min-w-max" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 transition-colors whitespace-nowrap flex-shrink-0
                      ${isActive
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden text-xs">{tab.label.split(' ')[0]}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[65vh] sm:max-h-[60vh]">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                {/* User Avatar and Basic Info */}
                <div className="flex items-center space-x-4 sm:space-x-6">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-indigo-600 flex items-center justify-center">
                      <User className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 truncate">
                      {user?.email?.split('@')[0] || 'User'}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      Note Taking App User
                    </p>
                  </div>
                </div>

                {/* Account Information */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Address
                    </label>
                    <div className="mt-1 flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{user?.email}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Member Since
                    </label>
                    <div className="mt-1 flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date().toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* Success Message */}
                {passwordChangeSuccess && (
                  <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Shield className="h-5 w-5 text-green-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                          Password Changed Successfully
                        </h3>
                        <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                          <p>Your password has been updated. Make sure to use your new password for future logins.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit(onPasswordSubmit)}>
                  {/* Global Error */}
                  {errors.root && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                      <div className="text-sm text-red-700 dark:text-red-300">
                        {errors.root.message}
                      </div>
                    </div>
                  )}

                  {/* Email Field (Read-only) */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Address
                    </label>
                    <input
                      {...register('email')}
                      type="email"
                      readOnly
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Your email address cannot be changed.
                    </p>
                  </div>

                  {/* Current Password Field */}
                  <div>
                    <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Current Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        {...register('oldPassword', {
                          required: 'Current password is required'
                        })}
                        type={showOldPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        className={`block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm ${errors.oldPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowOldPassword(!showOldPassword)}
                      >
                        {showOldPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.oldPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.oldPassword.message}</p>
                    )}
                  </div>

                  {/* New Password Field */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      New Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        {...register('newPassword', {
                          required: 'New password is required',
                          minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters'
                          },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/,
                            message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
                          }
                        })}
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className={`block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm ${errors.newPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                        placeholder="Enter your new password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.newPassword.message}</p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Password must be at least 8 characters with uppercase, lowercase, and number.
                    </p>
                  </div>

                  {/* Confirm New Password Field */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Confirm New Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        {...register('confirmPassword', {
                          required: 'Please confirm your new password',
                          validate: value => value === newPassword || 'Passwords do not match'
                        })}
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        className={`block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm ${errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                        placeholder="Confirm your new password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errors.confirmPassword.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => reset()}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isChangingPassword}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isChangingPassword ? (
                        <>
                          <ButtonSpinner className="mr-2" />
                          Changing...
                        </>
                      ) : (
                        'Change Password'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* AI Settings Tab */}
            {activeTab === 'ai' && (
              <div className="space-y-6">
                <div className="flex items-center">
                  <Brain className="h-5 w-5 text-purple-600 mr-2" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      Text Generation AI Configuration
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Configure your AI assistant settings. Leave fields empty to use application defaults.
                    </p>
                  </div>
                </div>

                {isLoadingAiConfig ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* AI URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Hugging Face / AI URL
                      </label>
                      <input
                        type="url"
                        value={aiConfig.aiUrl}
                        onChange={(e) => handleAiConfigChange('aiUrl', e.target.value)}
                        placeholder="https://router.huggingface.co/v1/chat/completions"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        The API endpoint for your AI service
                      </p>
                    </div>

                    {/* Authorization Token */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Authorization Token
                      </label>
                      <input
                        type="password"
                        value={aiConfig.aiToken}
                        onChange={(e) => handleAiConfigChange('aiToken', e.target.value)}
                        placeholder="Bearer hf_mxKlNDetKHzAqeWRttaGAFVvTruDor"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Your API authorization token (kept secure)
                      </p>
                    </div>

                    {/* AI Model */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        AI Model
                      </label>
                      <input
                        type="text"
                        value={aiConfig.aiModel}
                        onChange={(e) => handleAiConfigChange('aiModel', e.target.value)}
                        placeholder="meta-llama/Llama-3.2-3B-Instruct:novita"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        The AI model to use for text generation
                      </p>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-center sm:justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={saveAIConfiguration}
                        disabled={isSavingAiConfig}
                        className="w-full sm:w-auto flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      >
                        {isSavingAiConfig ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Configuration
                          </>
                        )}
                      </button>
                    </div>

                    {/* Info Notice */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Configuration Info
                          </h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            These settings will be used in the Rich Text Editor's AI assistant feature. 
                            If you leave any field empty, the application's default values will be used.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cloud Storage Tab */}
            {activeTab === 'cloud' && (
              <div className="space-y-6">
                <CloudStorageSettings />
                
                {/* Google Drive Maintenance Section */}
                {!isLocalMode && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="flex items-center mb-4">
                      <Trash2 className="h-5 w-5 text-orange-600 mr-2" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                          Google Drive Maintenance
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Clean up corrupted files that may be causing backend performance issues.
                        </p>
                      </div>
                    </div>

                    {/* Cleanup Statistics */}
                    {cleanupStats && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Storage Analysis
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Total Files:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{cleanupStats.totalFiles}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Suspicious Files:</span>
                            <span className={`ml-2 font-medium ${
                              cleanupStats.suspiciousFiles > 0 
                                ? 'text-orange-600 dark:text-orange-400' 
                                : 'text-green-600 dark:text-green-400'
                            }`}>
                              {cleanupStats.suspiciousFiles}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Soft-Deleted Files:</span>
                            <span className={`ml-2 font-medium ${
                              cleanupStats.softDeletedFiles > 0 
                                ? 'text-purple-600 dark:text-purple-400' 
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {cleanupStats.softDeletedFiles}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Empty Files:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{cleanupStats.emptyFiles}</span>
                          </div>
                        </div>
                        {cleanupStats.suspiciousFiles > 0 && (
                          <div className="mt-3 text-xs text-orange-600 dark:text-orange-400">
                            ⚠️ Corrupted files detected. These may be causing backend JSON parsing errors and performance issues.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cleanup Actions */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={loadCleanupStats}
                        disabled={isLoadingStats || isCleaningUp}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                      >
                        <RefreshCw className={`h-4 w-4 ${isLoadingStats ? 'animate-spin' : ''}`} />
                        <span>
                          {isLoadingStats ? 'Scanning...' : 'Scan Files'}
                        </span>
                      </button>
                      
                      {cleanupStats && cleanupStats.suspiciousFiles > 0 && (
                        <button
                          onClick={handleCleanupFiles}
                          disabled={isCleaningUp || isLoadingStats}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                          <Trash2 className={`h-4 w-4 ${isCleaningUp ? 'animate-pulse' : ''}`} />
                          <span>
                            {isCleaningUp ? 'Cleaning...' : `Clean Up ${cleanupStats.suspiciousFiles} Files`}
                          </span>
                        </button>
                      )}

                      {/* Folder Cleanup Button */}
                      <button
                        onClick={handleCleanupFolders}
                        disabled={isCleaningUp || isLoadingStats}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        title="Clean up duplicate/corrupted folders that may be causing sync issues"
                      >
                        <Trash2 className={`h-4 w-4 ${isCleaningUp ? 'animate-pulse' : ''}`} />
                        <span>
                          {isCleaningUp ? 'Cleaning...' : 'Clean Folders'}
                        </span>
                      </button>

                      {/* Empty Bin Button */}
                      <button
                        onClick={handleEmptyBin}
                        disabled={isCleaningUp || isLoadingStats || !cleanupStats?.softDeletedFiles}
                        className={`px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2 ${
                          cleanupStats?.softDeletedFiles > 0
                            ? 'bg-purple-600 hover:bg-purple-700'
                            : 'bg-gray-400 cursor-not-allowed'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={cleanupStats?.softDeletedFiles > 0 
                          ? `Permanently delete ${cleanupStats.softDeletedFiles} files from the soft-delete folder (recycle bin)`
                          : 'No files in recycle bin to delete'
                        }
                      >
                        <Trash2 className={`h-4 w-4 ${isCleaningUp ? 'animate-pulse' : ''}`} />
                        <span>
                          {isCleaningUp ? 'Emptying...' : 
                           cleanupStats?.softDeletedFiles > 0 
                             ? `Empty Bin (${cleanupStats.softDeletedFiles})`
                             : 'Empty Bin (0)'
                          }
                        </span>
                      </button>
                    </div>

                    {/* Info Notice */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mt-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            About Cleanup
                          </h4>
                          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                            The "Scan Files" button now performs a comprehensive scan of your entire Google Drive structure, 
                            including all subfolders. It detects empty files, duplicates, files in wrong locations, and orphaned files 
                            that may be causing sync issues. Files are moved to trash, not permanently deleted.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Folder Cleanup Notice */}
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mt-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                            About Folder Cleanup
                          </h4>
                          <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                            The "Clean Folders" button removes duplicate or corrupted Google Drive folders that may be causing sync issues. 
                            This will reset your folder structure - use only if you're experiencing folder-related problems.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Empty Bin Notice */}
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4 mt-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-purple-800 dark:text-purple-200">
                            About Empty Bin
                          </h4>
                          <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                            The "Empty Bin" button <strong>permanently deletes</strong> all files from the soft-delete folder (recycle bin). 
                            This action cannot be undone and will free up storage space. Use this when you're sure you don't need to recover any deleted notes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Orphaned Files Cleanup Section */}
                {!isLocalMode && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                    <div className="flex items-center mb-4">
                      <Trash2 className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                          Orphaned Files Cleanup
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Clean up orphaned images and attachments that are no longer referenced in any notes.
                        </p>
                      </div>
                    </div>

                    {/* Orphaned Files Statistics */}
                    {orphanedFilesStats && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Orphaned Files Analysis
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Total Images:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{orphanedFilesStats.totalImages}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Total Attachments:</span>
                            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{orphanedFilesStats.totalAttachments}</span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Orphaned Images:</span>
                            <span className={`ml-2 font-medium ${
                              orphanedFilesStats.orphanedImages > 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {orphanedFilesStats.orphanedImages}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Orphaned Attachments:</span>
                            <span className={`ml-2 font-medium ${
                              orphanedFilesStats.orphanedAttachments > 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {orphanedFilesStats.orphanedAttachments}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Orphaned Cache Entries:</span>
                            <span className={`ml-2 font-medium ${
                              orphanedFilesStats.orphanedCacheEntries > 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {orphanedFilesStats.orphanedCacheEntries}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Referenced Files:</span>
                            <span className="ml-2 font-medium text-blue-600 dark:text-blue-400">
                              {orphanedFilesStats.referencedFileIds + orphanedFilesStats.referencedAttachmentIds}
                            </span>
                          </div>
                        </div>
                        {(orphanedFilesStats.orphanedImages > 0 || orphanedFilesStats.orphanedAttachments > 0 || orphanedFilesStats.orphanedCacheEntries > 0) && (
                          <div className="mt-3 text-xs text-green-600 dark:text-green-400">
                            ✅ Orphaned files detected. These files are no longer referenced in any notes and can be safely deleted to free up storage space.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Orphaned Files Cleanup Actions */}
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={loadOrphanedFilesStats}
                        disabled={isLoadingOrphanedStats || isCleaningUp}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                      >
                        <RefreshCw className={`h-4 w-4 ${isLoadingOrphanedStats ? 'animate-spin' : ''}`} />
                        <span>
                          {isLoadingOrphanedStats ? 'Scanning...' : 'Scan Orphaned Files'}
                        </span>
                      </button>
                      
                      {orphanedFilesStats && (orphanedFilesStats.orphanedImages > 0 || orphanedFilesStats.orphanedAttachments > 0 || orphanedFilesStats.orphanedCacheEntries > 0) && (
                        <button
                          onClick={handleCleanupOrphanedFiles}
                          disabled={isCleaningUp || isLoadingOrphanedStats}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                          <Trash2 className={`h-4 w-4 ${isCleaningUp ? 'animate-pulse' : ''}`} />
                          <span>
                            {isCleaningUp ? 'Cleaning...' : 
                             `Clean Orphaned Files (${orphanedFilesStats.orphanedImages + orphanedFilesStats.orphanedAttachments + orphanedFilesStats.orphanedCacheEntries})`
                            }
                          </span>
                        </button>
                      )}
                    </div>

                    {/* Orphaned Files Info Notice */}
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 mt-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                            About Orphaned Files Cleanup
                          </h4>
                          <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                            Orphaned files are images and attachments that are no longer referenced in any notes. 
                            This can happen when notes are deleted or when files are uploaded but not properly linked. 
                            Cleaning up orphaned files will free up storage space and improve performance.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Backup and Restore Tab */}
            {activeTab === 'backup' && (
              <div className="space-y-6">
                <div className="flex items-center">
                  <Database className="h-5 w-5 text-blue-600 mr-2" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      Backup and Restore
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Manage your notes backup and restore your data from previous backups.
                    </p>
                  </div>
                </div>

                {/* Backup Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                    Create Backup
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Export all your notes, folders, and attachments to a ZIP backup file that you can download and store safely. 
                    Sensitive note content will remain encrypted and can only be decrypted with the original password.
                  </p>
                  <button 
                    onClick={handleCreateBackup}
                    disabled={isCreatingBackup}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                  >
                    {isCreatingBackup ? (
                      <ButtonSpinner />
                    ) : (
                      <Database className="h-4 w-4" />
                    )}
                    <span>{isCreatingBackup ? 'Creating Backup...' : 'Create Backup'}</span>
                  </button>
                </div>

                {/* Restore Section */}
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                    Restore from Backup
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Upload a NoteSpark backup ZIP file to restore your notes and folders. 
                    Existing data will be preserved - only new items from the backup will be added.
                  </p>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleFileSelection}
                      disabled={isRestoring}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 dark:file:bg-amber-900/40 dark:file:text-amber-300 disabled:opacity-50"
                    />
                    
                    {/* Progress indicator */}
                    {isRestoring && (
                      <div className="space-y-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                            Restoring backup...
                          </span>
                          <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                            {restoreProgress.percent}%
                          </span>
                        </div>
                        <div className="w-full bg-amber-200 dark:bg-amber-800 rounded-full h-3">
                          <div 
                            className="bg-amber-600 dark:bg-amber-400 h-3 rounded-full transition-all duration-300 ease-out" 
                            style={{ width: `${restoreProgress.percent}%` }}
                          />
                        </div>
                        <p className="text-sm text-amber-700 dark:text-amber-300 text-center">
                          {restoreProgress.message}
                        </p>
                      </div>
                    )}
                    
                    <button 
                      onClick={handleRestoreBackup}
                      disabled={!selectedFile || isRestoring}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                    >
                      {isRestoring ? (
                        <ButtonSpinner />
                      ) : (
                        <Database className="h-4 w-4" />
                      )}
                      <span>{isRestoring ? 'Restoring...' : 'Restore Backup'}</span>
                    </button>
                    
                    {selectedFile && !isRestoring && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>
                </div>

                {/* Warning Section */}
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-orange-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-md font-medium text-orange-900 dark:text-orange-200 mb-2">
                        Important Information
                      </h4>
                      <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
                        <li>• <strong>Sensitive content encryption:</strong> Sensitive note content will remain encrypted in backups and can only be decrypted with the original password used to encrypt each note</li>
                        <li>• <strong>No overwrites:</strong> Restoring preserves your existing data - only new items from backup will be added</li>
                        <li>• <strong>Offline operation:</strong> All backup and restore operations work entirely offline in your browser</li>
                        <li>• <strong>Trusted sources:</strong> Only restore backup files from trusted sources</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsModal 